import React from 'react';

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouteError,
} from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useAppSelector } from './reducers/hooks';

import { ParallaxProvider, config } from '@adc/parallax-component-library';

import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';

import Dashboard from './components/pages/Dashboard';
import Organizations from './components/pages/Organizations';
import Studies from './components/pages/Studies';
import Study from './components/pages/Study';
import StudyCreate from './components/pages/StudyCreate';
import Site from './components/pages/Site';
import Subject from './components/pages/Subject';
import Sensor from './components/pages/Sensor';
import UnexpectedError from './components/utility/UnexpectedError';
import NotFound from './components/utility/NotFound';
import Accounts from './components/pages/Accounts';
import RoleAssignments from './components/pages/RoleAssignments';
import { Account } from './components/pages/Account';
import { ROUTES } from './constants';

const queryClient = new QueryClient();

/**
 * Prevent the user from accessing protected routes if they are not logged in.
 * @returns {React.JSX.Element}
 */
function ProtectedRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  if (!isLoggedIn) {
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  return <Outlet />;
}

/**
 * Prevent the user from accessing auth routes if they are already logged in.
 * @returns {React.JSX.Element}
 */
function AuthRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  if (isLoggedIn) {
    return <Navigate to={ROUTES.ROOT} replace />;
  }

  return <Outlet />;
}

function RouterErrorMessage() {
  const routeError = useRouteError();

  return <UnexpectedError errorMessage={String(routeError)} />;
}

const routes = [
  {
    path: ROUTES.AUTH.ROOT,
    element: <AuthLayout />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <AuthRoute />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.AUTH.LOGIN} replace />,
          },
          { path: ROUTES.AUTH.LOGIN, element: <LoginForm /> },
          {
            path: ROUTES.AUTH.FORGOT_PASSWORD,
            element: <ForgotPassword />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: ROUTES.ROOT,
    element: <ProtectedRoute />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.DASHBOARD.ROOT} replace />,
          },
          { path: ROUTES.DASHBOARD.ROOT, element: <Dashboard /> },
          { path: ROUTES.ORGANIZATIONS.ROOT, element: <Organizations /> },
          {
            path: ROUTES.ACCOUNTS.ROOT,
            children: [
              { index: true, element: <Accounts /> },
              { path: ROUTES.ACCOUNTS.DETAIL, element: <Account /> },
            ],
          },
          {
            path: ROUTES.ROLE_ASSIGNMENTS.ROOT,
            children: [{ index: true, element: <RoleAssignments /> }],
          },
          {
            path: ROUTES.STUDIES.ROOT,
            children: [
              { index: true, element: <Studies /> },
              { path: ROUTES.STUDIES.CREATE, element: <StudyCreate /> },
              { path: ROUTES.STUDIES.DETAIL, element: <Study /> },
            ],
          },
          {
            path: ROUTES.SITES.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SITES.DETAIL, element: <Site /> },
            ],
          },
          {
            path: ROUTES.SUBJECTS.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SUBJECTS.DETAIL, element: <Subject /> },
            ],
          },
          {
            path: ROUTES.SENSORS.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SENSORS.DETAIL, element: <Sensor /> },
            ],
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes);

/**
 * The main application component that sets up the routing and layout
 * @returns {React.JSX.Element}
 */
export function App() {
  return (
    <ParallaxProvider
      config={config}
      locale="en-US"
      dir="ltr"
      defaultTheme="light"
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ParallaxProvider>
  );
}

export default App;
